import { useState } from "react";

import { useAcceptProposedWindow, useRejectProposedWindow } from "./api";
import { formatDate } from "./format";
import type { ProposedWindow } from "./types";

// Phase 4c availability-procurement FR-5. Banner that surfaces an
// admin-proposed window change on a procurement_pending order.
// Customer confirms or rejects; on success the banner disappears
// because the parent query is invalidated.
export function ProposedWindowBanner({ orderNumber, proposal }:
                                     { orderNumber: string; proposal: ProposedWindow }): JSX.Element {
  const accept = useAcceptProposedWindow(orderNumber);
  const reject = useRejectProposedWindow(orderNumber);
  const [error, setError] = useState<string | null>(null);

  function run(p: Promise<unknown>) {
    setError(null);
    p.catch((e: unknown) => {
      if (e instanceof Error) setError(e.message);
    });
  }

  return (
    <div className="rounded border border-purple-300 bg-purple-50 p-3"
         role="status" aria-live="polite"
         data-testid="proposed-window-banner">
      <h2 className="text-sm font-semibold text-purple-900">
        JDR is sourcing equipment for your rental
      </h2>
      <p className="mt-1 text-sm text-purple-900">
        We can deliver{" "}
        <strong>{formatDate(proposal.proposed_start_at)} – {formatDate(proposal.proposed_end_at)}</strong>
        {" "}— slightly different from your original window{" "}
        <span className="text-purple-700">
          ({formatDate(proposal.original_start_at)} – {formatDate(proposal.original_end_at)})
        </span>.
      </p>
      {proposal.admin_note ? (
        <p className="mt-1 text-xs italic text-purple-800"
           data-testid="proposed-window-note">
          A note from us: {proposal.admin_note}
        </p>
      ) : null}
      {proposal.responds_by_at ? (
        <p className="mt-1 text-xs text-purple-700">
          Please respond by {formatDate(proposal.responds_by_at)}.
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button"
                className="rounded bg-purple-700 px-3 py-1.5 text-sm text-white disabled:opacity-60"
                data-testid="proposed-window-accept"
                onClick={() => run(accept.mutateAsync())}
                disabled={accept.isPending || reject.isPending}>
          Accept new dates
        </button>
        <button type="button"
                className="rounded border border-purple-300 px-3 py-1.5 text-sm text-purple-900 disabled:opacity-60"
                data-testid="proposed-window-reject"
                onClick={() => run(reject.mutateAsync())}
                disabled={accept.isPending || reject.isPending}>
          Decline and cancel
        </button>
      </div>

      {error ? (
        <p className="mt-2 text-sm text-red-700" data-testid="proposed-window-error">{error}</p>
      ) : null}
    </div>
  );
}
